import {
  Component,
  ElementRef,
  Inject,
  Injector,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, concatMap, map } from 'rxjs';
import {
  Counter,
  NegotiationDetails,
} from 'src/app/components/negotiations/negotiations.model';
import { MatExpansionModule } from '@angular/material/expansion';
import { SplitLocationPipe } from 'src/app/pipes/split-location.pipe';
import { LotState } from 'src/app/store/lots-library/lots.reducer';
import { UserDetails } from 'src/app/components/login/user.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NegotiationDetailsDialogComponent } from 'src/app/components/custom/negotiation-details-dialog/negotiation-details-dialog.component';
import { NegotiationDetailsService } from 'src/app/services/negotiation-details.service';
import { NegotiationActionsService } from 'src/app/services/negotiation-actions.service';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { CreateNegotiationCounterService } from 'src/app/services/create-negotiation-counter.service';
import { UserState } from 'src/app/store/user/user.reducer';
import { selectLot } from 'src/app/store/lots-library/lot.actions';
import {
  BasisPrice,
  CashPrice,
  Lot,
  MarketType,
  PriceType,
  PriceUnion,
} from '../../lots.model';
import { LotsService } from 'src/app/services/lots.service';
import {
  DialogType,
  LotActionDialogTemplateComponent,
} from './lot-action-dialog-template/lot-action-dialog-template.component';
import {
  fetchData,
  removeRow,
} from 'src/app/store/table-data/table-data.actions';
import { PriceTypePipe } from 'src/app/pipes/price-type.pipe';
import { CommonModule } from '@angular/common';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { selectUser } from 'src/app/store/user/user.selectors';
import { selectActiveLotData } from 'src/app/store/lots-library/lot.selectors';
import { fetchNegotiations } from 'src/app/store/negotiations/negotiations.actions';

@Component({
  selector: 'app-negotiation-details-card',
  imports: [
    CommonModule,
    ScrollingModule,
    PriceTypePipe,
    RouterModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatExpansionModule,
    MatSelectModule,
    MatTableModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    FontAwesomeModule,
  ],
  templateUrl: './negotiation-details-card.component.html',
  styleUrl: './negotiation-details-card.component.css',
})
export class NegotiationDetailsCardComponent implements OnInit {
  @ViewChild('myInput') myInput: ElementRef;
  private analytics = inject(Analytics);
  negotiations$: Observable<LotState>;
  userDetails$: Observable<UserDetails>;
  btnStates = signal({
    disableAll: false,
    showDecline: false,
    disableDecline: false,
    disableCounter: false,
    disableAccept: false,
    showRevoke: false,
  });

  isNewNegotiation = signal<boolean>(false);
  userId = signal<string>('');
  priceType = signal<PriceType>(null);
  lotId: string = null;
  negotiationId = signal<string>('');
  lot = signal<Lot>(null);
  activeCounter = signal<Counter>({
    created_at: null,
    originator_id: '',
    price_value: null,
    volume_value: 0,
    ship_begin: '',
    ship_end: '',
  });

  counterForm = this.fb.group({
    // originator_id: [''],
    lot_id: [''],
    price_type: [''],
    cash_price: this.fb.group({
      amount: [0],
    }),
    basis_price: this.fb.group({
      basisPoints: [0, Validators.required],
      month: ['', Validators.required],
      product_code: ['', Validators.required],
      year: [0, Validators.required],
    }),
    volume_value: [0],
    ship_begin: [''],
    ship_end: [''],
  });
  initalFormVals: object;

  dialogConfig = new MatDialogConfig();
  horizontalPosition: MatSnackBarHorizontalPosition = 'center';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';
  constructor(
    private store: Store<{ user: UserState; activeLot: LotState }>,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private negotiationsDetailsService: NegotiationDetailsService, // don't need details? delete
    private negotiationsActionsService: NegotiationActionsService,
    private createNegotiationCounterService: CreateNegotiationCounterService,
    private lotsService: LotsService,
    private snackBar: MatSnackBar,
    private injector: Injector,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.userDetails$ = this.store.select(selectUser).pipe(
      map((user) => {
        if (user) {
          this.userId.set(user._id);
        }
        return user;
      })
    );
    this.negotiations$ = this.store.select(selectActiveLotData).pipe(
      map((res) => {
        if (res.lot) {
          this.activeCounter.update((values) => ({
            ...values,
            ship_begin: '',
            ship_end: '',
            price_value: null,
          }));
          this.counterForm.reset();
          this.lot.set({ ...res.lot });
          this.priceType.set(
            this.lotsService.isCashPrice(res.lot.price as PriceUnion)
              ? PriceType.Cash
              : PriceType.Basis
          );
          this.lotId = res.lot._id;
          if (res.negotiationDetails.length !== 0) {
            this.isNewNegotiation.set(false);
            this.btnStates.update((prev) => ({
              ...prev,
              showRevoke: this.userId() === res.negotiationDetails[0].user_id,
              disableAll: res.negotiationDetails[0].status === 'Declined',
            }));
            this.activeCounter.set(res.negotiationDetails[0].counters[0]);
            this.negotiationId.set(res.negotiationDetails[0]._id);
            this.setCounterForm(this.activeCounter());
          } else {
            this.isNewNegotiation.set(true);
            this.setCounterForm();
          }
        }
        // const negotiationDetails = res.negotiationDetails[0];
        return res;
      })
    );
    this.formChanges('ngOnInit');
  }

  counter() {
    //open dialog counter
    const counterDialogRef = this.dialog.open(
      LotActionDialogTemplateComponent,
      {
        injector: this.injector,
        data: {
          form: this.counterForm,
          lotId: this.lotId,
          dialogTitle: 'Counter',
          dialogType: DialogType.Counter,
        },
      }
    );

    counterDialogRef.afterClosed().subscribe({
      next: (result) => {
        // create negotiation counter if dialog result is true
        // return;
        if (result.confirm) {
          logEvent(this.analytics, LogEventsEnum.negotiations_form_submit_tap);
          this.counterForm.patchValue({ ...result.data });
          let values = this.counterForm.value;
          values = this.lotsService.removeProperty(
            values,
            values.price_type === PriceType.Basis ? 'cash_price' : 'basis_price'
          );

          // check if lot or negotiation already exists
          if (this.isNewNegotiation()) {
            // fire mutation to add negotiation
            this.negotiationsActionsService
              .createNegotiation({
                ...values,
                lot_id: this.lot()._id,
              })
              .subscribe({
                next: (result) => {
                  // console.log('new nego response', result.data);
                  this.counterForm.setErrors({ sameValues: false });
                  // Refresh the active lot's negotiations so the submitter's
                  // card updates immediately (mirrors the socket newCounter path).
                  this.store.dispatch(selectLot({ lotDetails: this.lot() }));
                  this.store.dispatch(fetchNegotiations());
                  this.snackBar.open(`Created counter`, 'Close', {
                    duration: 3500,
                    panelClass: ['blue-snackbar'],
                  });
                },
                error: (err) => {
                  this.snackBar.open(`Error creating counter`, 'Close', {
                    duration: 3500,
                    panelClass: ['blue-snackbar'],
                  });
                },
              });
          } else {
            /* TODO
      this only creates a counter for the first Negotiation in Negotiations Array.
      Handle case where there are multiple Negotiations and user selects which Negotiation to counter
    */
            // send mutation for adding Counter to Negotiation
            // console.log('add counter to negotiation', this.counterForm.value);
            this.createNegotiationCounterService
              .mutate({
                id: this.negotiationId(), //  negotiation id
                input: { ...values },
              })
              .subscribe({
                next: (response) => {
                  // console.log(response);
                  this.counterForm.setErrors({ sameValues: false });
                  // Refresh the active lot's negotiations so the submitter's
                  // card updates immediately (mirrors the socket newCounter path).
                  this.store.dispatch(selectLot({ lotDetails: this.lot() }));
                  this.store.dispatch(fetchNegotiations());
                  this.snackBar.open(`Created counter`, 'close', {
                    duration: 3500,
                    panelClass: ['blue-snackbar'],
                  });
                  // make material snackbar confirmation
                },
                error: (response) => {
                  this.snackBar.open(
                    `Error creating counter ${response}`,
                    'Close',
                    {
                      duration: 3500,
                      panelClass: ['blue-snackbar'],
                    }
                  );
                },
              });
          }
        }
      },
    });
  }

  acceptNegotiation() {
    this.dialogConfig.data = {
      dialogText: 'Are you sure you want to accept Counter?',
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log('contract dialog close result', this.negotiationId());
        if (result.confirm) {
          this.negotiationsActionsService
            .createContract({
              // lot_id: this.lotId, // only pass negotiation_id
              negotiation_id: this.negotiationId(),
            })
            .subscribe({
              next: (result) => {
                // console.log('contract creation result', result);
                this.snackBar.open(
                  'Negotiation accepted. Contract Status Pending',
                  null,
                  {
                    horizontalPosition: this.horizontalPosition,
                    verticalPosition: this.verticalPosition,
                    panelClass: ['blue-snackbar'],
                    duration: 4500,
                  }
                );
                // this.store.dispatch(removeRow({ lotId: this.lotId }));
                /**
                  TODO : change so that it fetch for active app state market
                */
                this.store.dispatch(
                  fetchData({ limit: 50, marketType: MarketType.Feed })
                );
                // this.router.navigate(['/trades']);
              },
              error: (err) => {
                console.error('Error accepting negotiation:', err.message);
                this.snackBar.open('Error accepting negotiation', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  panelClass: ['blue-snackbar'],
                  duration: 3000,
                });
              },
            });
        }
      },
    });
  }

  decline() {
    this.dialogConfig.data = {
      dialogText: 'Are you sure you want to decline the Negotiation?',
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log('decline dialog action', result);
        // yes or confirm continue
        if (result.confirm) {
          // console.log("dialog confirm revoke; Sending Revoke query");
          this.negotiationsActionsService
            .updateNegotiation(this.negotiationId(), { status: 'Declined' })
            .subscribe({
              next: (result) => {
                // console.log("revoke query result:",result.data);
                this.snackBar.open('Negotiation declined', 'close', {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 4000,
                  panelClass: ['blue-snackbar'],
                });
              },
              error: (err) => {
                console.error('Error revoking negotiation:', err.message);
                this.snackBar.open('Negotiation revoked', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 4000,
                });
              },
            });
        }
      },
    });
  }

  selectNegotiation(e) {
    // console.log('select nego', e)
    this.negotiationId.set(e._id);
    this.activeCounter.set({ ...e.counters[0] });
    this.setCounterForm(this.activeCounter());
  }

  setCounterForm(data: Counter = null) {
    // if negotiationDetails have counters
    if (data) {
      if (this.activeCounter().originator_id === this.userId()) {
        this.counterForm.disable({ emitEvent: false });
        this.btnStates.update((obj) => ({
          ...obj,
          showDecline: false,
          disableCounter: true,
          disableAccept: true,
        }));
      } else {
        this.counterForm.enable({ emitEvent: false });
        this.btnStates.update((obj) => ({
          ...obj,
          showDecline: true,
          disableCounter: false,
          disableAccept: false,
        }));
      }
      this.counterForm.patchValue(
        {
          price_type: this.lotsService.isCashPrice(
            data.price_value as PriceUnion
          )
            ? PriceType.Cash
            : PriceType.Basis,

          ...(this.priceType() === PriceType.Cash
            ? {
                cash_price: {
                  ...(this.activeCounter().price_value as CashPrice),
                },
                basis_price: null,
              }
            : {
                basis_price: {
                  ...(this.activeCounter().price_value as BasisPrice),
                },
                cash_price: null,
              }),
          volume_value: data.volume_value || 0,
          ship_begin: data.ship_begin || '',
          ship_end: data.ship_end || '',
        },
        { emitEvent: false }
      );
    } else {
      if (this.lot().user_id === this.userId()) {
        this.counterForm.reset({}, { emitEvent: false });
        this.counterForm.disable({ emitEvent: false });
        this.activeCounter.set({
          created_at: null,
          originator_id: '',
          price_value: null,
          volume_value: 0,
          ship_begin: '',
          ship_end: '',
        });
      } else {
        this.counterForm.patchValue(
          {
            volume_value: this.lot().volume_value,
            ship_begin: `${this.lot().ship_begin}`,
            ship_end: `${this.lot().ship_end}`,
            price_type: this.priceType(),

            ...(this.priceType() === PriceType.Cash
              ? {
                  cash_price: {
                    ...(this.lot().price as CashPrice),
                  },
                  basis_price: null,
                }
              : {
                  basis_price: {
                    ...(this.lot().price as BasisPrice),
                  },
                  cash_price: null,
                }),
          },
          { emitEvent: false }
        );

        this.counterForm.enable({ emitEvent: false });
      }
    }
    this.initalFormVals = { ...this.counterForm.value };
  }

  formChanges(source = null): void {
    this.counterForm.valueChanges.subscribe((vals) => {
      // console.log('init form', this.initalFormVals);
      // console.log('form changes', vals);
      if (this.counterForm.enabled) {
        if (JSON.stringify(vals) === JSON.stringify(this.initalFormVals)) {
          // console.log('form has same initial values,cannot counter');
          this.counterForm.setErrors({ sameValues: true });
        } else {
          this.counterForm.setErrors({ sameValues: false });
        }
      }
    });
  }

  revoke() {
    const revokeDialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      {
        data: { dialogText: 'Are you sure you want to revoke negotiation?' },
      }
    );

    revokeDialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log('revoke confirm', result);
        if (result.confirm) {
          // console.log("dialog confirm revoke; Sending Revoke query");
          this.negotiationsActionsService
            .revoke(this.negotiationId())
            .subscribe({
              next: (res) => {
                // console.log('revoke query result:', res.data);
                this.store.dispatch(selectLot({ lotDetails: this.lot() }));
                this.snackBar.open('Negotiation revoked', 'close', {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 4000,
                  panelClass: ['blue-snackbar'],
                });
              },
              error: (err) => {
                this.snackBar.open('Error revoking', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 4000,
                  panelClass: ['blue-snackbar'],
                });
              },
            });
        }
      },
    });
  }

  focusOnInput(): void {
    this.counter();
    // TODO : delete below after confirming dialog counter works
    // this.renderer.setAttribute(this.myInput.nativeElement, 'autofocus', 'true');
    // this.myInput.nativeElement.scrollIntoView({ behavior: 'smooth' });

    // this.myInput.nativeElement.focus({ behavior: 'smooth' });
  }

  getPriceText(price: PriceUnion): number {
    if (!price) {
      return 0.0;
    }
    switch (this.lotsService.isCashPrice(price)) {
      case true:
        return (price as CashPrice).amount;
      case false:
        return (price as BasisPrice).basisPoints;
      default:
        return 0.0;
    }
  }
}
